import { Component, EventEmitter, Input, Output, Signal, effect, inject } from '@angular/core';
import { AuthService } from '@core/services/auth.service';
import { Role } from '@core/enums/roles';
import { ILoginResponse } from '@core/interfaces/ilogin-response';
import { INavbarItems } from '@core/interfaces/inavbar-items';
import { NavigationEnd, Router } from '@angular/router';
import { Navbar } from '@core/enums/navbar';
import { filter } from 'rxjs';
import { env } from 'node:process';
import { environment } from 'src/environments/environment';
@Component({
  selector: 'prism-navbar',
  standalone: false,
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss'
})
export class NavbarComponent {
  private router = inject(Router);
  private authService = inject(AuthService);

  @Input() openSideMenu: boolean = false;
  @Input() isFooter: boolean = false;
  @Output() openLoginModal: EventEmitter<any> = new EventEmitter();
  @Output() toggleSideMenu: EventEmitter<any> = new EventEmitter();
  @Output() closeSideMenu: EventEmitter<void> = new EventEmitter();

  currentUser: Signal<ILoginResponse | null> = this.authService.currentUserInfo;
  navbarItems: INavbarItems[] = [];

  get isLoggedIn(): boolean {
    return !!this.currentUser() && this.authService.checkLogin();
  }

  Navbar = Navbar;
  role = Role;
  logoUrl = 'assets/images/prism-logo-secondary-rgb.png';
  iconImgUrl: string = 'assets/images/Icon feather-user.svg';
  userFullName: string = '';
  activeLink: string = '';
  dropdownVisible = false;
  parentLink: string = '';
  index: number = -1;
  accountSettingsLink: string = '/account-settings';
  isSiteOffline = environment.isSiteOffline || false;

  constructor() {
    effect(() => {
      this.setMenuItems();
      this.iconImgUrl = this.isLoggedIn && this.currentUser() ?
        'assets/images/Icon feather-user.svg' :
        'assets/images/login icon.svg';
      this.userFullName = this.currentUser()?.fullName ?? '';
    });

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        this.activeLink = this.isSpecifiedRoute() ? this.router.url : '';
      });
  }

  setMenuItems(): void {
    if (this.isLoggedIn) {
      if (this.currentUser()?.roleId === Role.User) {
        this.navbarItems = [
          { label: 'Home', link: '/dashboard' },
          { label: 'About', link: '/about' },
          { label: 'FAQ', link: '/faq' },
          { label: 'Courses', link: '/courses' },
          {
            label: 'Resources', link: '/resources',
            childItems:
              [
                { label: 'Resources', link: 'resources' },
                { label: 'Facilitation Guide', link: 'resources/facilitation-guide' },
              ],
            open: false
          }
        ];
      } else if (this.currentUser()?.roleId === Role.DistrictAdmin || this.currentUser()?.roleId === Role.SystemAdmin) {
        this.navbarItems = [
          { label: 'Home', link: '/dashboard' },
          { label: 'About', link: '/about' },
          { label: 'FAQ', link: '/faq' },
          { label: 'Courses', link: '/courses' },
          {
            label: 'Resources',
            link: '/resources',
            childItems:
              [
                { label: 'Resources', link: 'resources' },
                { label: 'Facilitation Guide', link: 'resources/facilitation-guide' },
              ],
            open: false
          },
          {
            label: 'Management',
            link: '/user-management',
            childItems:
              [
                { label: 'User Management', link: '/user-management' },
                { label: 'District Management', link: '/district-management' },
                { label: 'School Management', link: '/school-management' },
              ],
            open: false
          },
          { label: 'Reports', link: '/report' }
        ];
      }
    } else {
      this.navbarItems = [
        { label: 'Home', link: '/home' },
        { label: 'About', link: '/about' },
        { label: 'FAQ', link: '/faq' },
        // { label: 'Development', link: '/development' },
        {
          label: 'Resources',
          link: '/resources'
          // childItems:
          //   [
          //     { label: 'Resources', link: 'resources/external' },
          //     { label: 'Impact Videos', link: 'resources/impact-video' },
          //   ],
          // open: false
        },
        // { label: 'Implementation', link: '/implementation' },
      ];
      if (this.isFooter) this.navbarItems.push({ label: 'Contact Us', link: '/contact-us' });
    }
  }

  navigate(url: string, toggleSideMenu: boolean = true): void {
    this.router.navigateByUrl(url);

    if (toggleSideMenu && this.openSideMenu) {
      this.handleSideMenuToggle();
    }
  }

  onLoginClick(): void {
    this.openLoginModal.emit();
  }

  isActive(link: string): boolean {
    return this.isSpecifiedRoute() && this.activeLink === link;
  }

  isSpecifiedRoute(): boolean {
    return this.navbarItems?.some(item => item.link === this.router.url) || this.router.url === '/contact-us';
  }

  toggleDropdown(menuItem: any): void {
    this.navbarItems.forEach(i => i.open = false);
    menuItem.open = !menuItem.open;
    menuItem.isFocused = true;
  }

  trackByIndex(index: number): number {
    return index;
  }

  handleDropdownKeyDown(event: KeyboardEvent, item: any): void {
    if (event.key === 'Enter' || event.key === ' ') {
      this.toggleDropdown(item);
      event.preventDefault();
    }
  }

  handleMenuItemKeyDown(event: KeyboardEvent, item: any): void {
    if (event.key === 'Enter') {
      const parentItem = this.navbarItems.find(menu => menu.childItems?.includes(item));
      if (parentItem) {
        parentItem.open = false;
      }
      this.navigate(item.link);
    }
  }

  onChildItemClick(parentItem: any, childItem: any): void {
    parentItem.open = false;
    this.navigate(childItem.link, false);
    this.closeSideMenu.emit();
  }

  logout(): void {
    this.authService.logout();
  }

  onFocusIn(menuItem: any): void {
    if (this.navbarItems[this.index]?.open) {
      this.navbarItems[this.index].open = this.parentLink === menuItem.label;
    }
    menuItem.isFocused = true;
    this.parentLink = menuItem.label;
  }

  onFocusOut(event: FocusEvent, menuItem: any): void {
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (!relatedTarget || !event.currentTarget || !(event.currentTarget as HTMLElement).contains(relatedTarget)) {
      menuItem.open = false;
    }
  }

  handleSideMenuToggle(): void {
    this.toggleSideMenu.emit();
  }
}
